import Pica from 'pica';

/**
 * Convert SVG element to optimized PNG file using Pica for high-quality resizing
 * Preserves aspect ratio while fitting within the specified maximum dimensions
 */
export const resizeDrawnSignature = async (
  svgElement: SVGSVGElement,
  args: { targetWidth: number; targetHeight: number; fileName: string },
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const { targetWidth: maxWidth, targetHeight: maxHeight, fileName } = args;
    const resizedCanvas = document.createElement('canvas');

    // Clone the SVG element to avoid modifying the original
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Ensure transparent background
    svgClone.style.background = 'transparent';

    // Remove any existing background rect elements that might have been added
    const backgroundRects = svgClone.querySelectorAll(
      'rect[fill="#ffffff"], rect[fill="white"], rect[fill="rgb(255,255,255)"]',
    );
    backgroundRects.forEach((rect) => rect.remove());

    // Get the original dimensions of the SVG
    const originalWidth = svgElement.getBoundingClientRect().width;
    const originalHeight = svgElement.getBoundingClientRect().height;

    // Get or preserve the original viewBox to avoid cropping
    const originalViewBox = svgElement.getAttribute('viewBox');
    const originalSvgWidth = svgElement.getAttribute('width');
    const originalSvgHeight = svgElement.getAttribute('height');

    // Calculate the scale factor to fit within max dimensions while maintaining aspect ratio
    const scaleX = maxWidth / originalWidth;
    const scaleY = maxHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate final dimensions that preserve aspect ratio
    const finalWidth = Math.round(originalWidth * scale);
    const finalHeight = Math.round(originalHeight * scale);

    // Preserve the original viewBox if it exists, otherwise create one from the original dimensions
    // This ensures we don't crop the SVG content
    if (originalViewBox) {
      svgClone.setAttribute('viewBox', originalViewBox);
    } else {
      // Use the actual SVG dimensions or fallback to bounding rect
      const viewBoxWidth = originalSvgWidth ? parseFloat(originalSvgWidth) : originalWidth;
      const viewBoxHeight = originalSvgHeight ? parseFloat(originalSvgHeight) : originalHeight;
      svgClone.setAttribute('viewBox', `0 0 ${viewBoxWidth} ${viewBoxHeight}`);
    }

    // Set width/height to original dimensions for the image conversion
    // The resizing will happen via Pica after converting to image
    svgClone.setAttribute('width', originalWidth.toString());
    svgClone.setAttribute('height', originalHeight.toString());

    const svgString = new XMLSerializer().serializeToString(svgClone);

    const img = new Image();
    img.onload = function () {
      // Set canvas to the calculated aspect-ratio-preserving dimensions
      resizedCanvas.width = finalWidth;
      resizedCanvas.height = finalHeight;

      const pica = Pica();
      pica
        .resize(img, resizedCanvas, {
          unsharpAmount: 160,
          unsharpRadius: 0.6,
          unsharpThreshold: 1,
        })
        .then((result) => pica.toBlob(result, 'image/png'))
        .then((resizedBlob) => {
          const file = new File([resizedBlob], fileName, {
            type: 'image/png',
            lastModified: Date.now(),
          });
          resolve(file);
        })
        .catch((error) => {
          console.error('Error resizing SVG:', error);
          reject(error);
        });
    };

    img.onerror = function (error) {
      console.error('Error loading SVG as image:', error);
      reject(error);
    };

    // Convert SVG to data URL and load as image
    img.src = 'data:image/svg+xml;base64,' + btoa(svgString);
  });
};
